import { supabase } from '../lib/supabase';
import { getStoredFingerprint } from '../utils/fingerprint';
import type { Issue, IssueComment, Vote } from '../types/issue';

export const issueService = {
  // Upload issue photo to storage
  async uploadIssuePhoto(file: File): Promise<string | null> {
    try {
      const fingerprint = getStoredFingerprint();
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `${fingerprint}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('issue-photos')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false
        });

      if (error) {
        console.error('Error uploading photo:', error);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('issue-photos')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error in uploadIssuePhoto:', error);
      return null;
    }
  },

  // Get all issues (legacy - no pagination)
  async getIssues(): Promise<Issue[]> {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  // Get issues with pagination
  async getIssuesPaginated(page: number = 0, limit: number = 10): Promise<{ issues: Issue[], hasMore: boolean }> {
    const from = page * limit;
    const to = from + limit - 1;
    
    const { data, error, count } = await supabase
      .from('issues')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    const totalCount = count || 0;
    const hasMore = (page + 1) * limit < totalCount;
    
    return { issues: data || [], hasMore };
  },

  // Get top issues sorted by upvotes (with pagination)
  async getTopIssuesPaginated(page: number = 0, limit: number = 10): Promise<{ issues: Issue[], hasMore: boolean }> {
    const from = page * limit;
    const to = from + limit - 1;
    
    const { data, error, count } = await supabase
      .from('issues')
      .select('*', { count: 'exact' })
      .order('upvotes', { ascending: false })
      .order('created_at', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    const totalCount = count || 0;
    const hasMore = (page + 1) * limit < totalCount;
    
    return { issues: data || [], hasMore };
  },

  // Create new issue
  async createIssue(issue: Omit<Issue, 'id' | 'upvotes' | 'downvotes' | 'created_at' | 'updated_at'>): Promise<Issue> {
    const fingerprint = getStoredFingerprint();
    
    const { data, error } = await supabase
      .from('issues')
      .insert({
        ...issue,
        user_fingerprint: fingerprint
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Vote on issue
  async voteOnIssue(issueId: string, voteType: 'upvote' | 'downvote'): Promise<void> {
    const fingerprint = getStoredFingerprint();
    
    try {
      // Check if user already voted
      const { data: existingVote, error: selectError } = await supabase
        .from('issue_votes')
        .select('*')
        .eq('issue_id', issueId)
        .eq('user_fingerprint', fingerprint)
        .maybeSingle();

      if (selectError) throw selectError;

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          // Remove vote if clicking same button
          const { error: deleteError } = await supabase
            .from('issue_votes')
            .delete()
            .eq('id', existingVote.id);
          
          if (deleteError) throw deleteError;
        } else {
          // Update existing vote
          const { error: updateError } = await supabase
            .from('issue_votes')
            .update({ vote_type: voteType })
            .eq('id', existingVote.id);
          
          if (updateError) throw updateError;
        }
      } else {
        // Create new vote
        const { error: insertError } = await supabase
          .from('issue_votes')
          .insert({
            issue_id: issueId,
            user_fingerprint: fingerprint,
            vote_type: voteType
          });
        
        if (insertError) throw insertError;
      }

      // Update vote counts on issue
      await this.updateIssueVoteCounts(issueId);
    } catch (error) {
      console.error('Error voting on issue:', error);
      throw error;
    }
  },

  // Update issue vote counts
  async updateIssueVoteCounts(issueId: string): Promise<void> {
    try {
      const { data: votes } = await supabase
        .from('issue_votes')
        .select('vote_type')
        .eq('issue_id', issueId);

      const upvoteCount = votes?.filter(v => v.vote_type === 'upvote').length || 0;
      const downvoteCount = votes?.filter(v => v.vote_type === 'downvote').length || 0;
      
      const { error } = await supabase
        .from('issues')
        .update({ 
          upvotes: upvoteCount, 
          downvotes: downvoteCount
        })
        .eq('id', issueId);

      if (error) {
        console.error('Error updating vote counts:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateIssueVoteCounts:', error);
      throw error;
    }
  },

  // Get comments for issue (legacy - no pagination)
  async getIssueComments(issueId: string): Promise<IssueComment[]> {
    const { data, error } = await supabase
      .from('issue_comments')
      .select('*')
      .eq('issue_id', issueId)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // Get comments with pagination
  async getIssueCommentsPaginated(issueId: string, page: number = 0, limit: number = 10): Promise<{ comments: IssueComment[], hasMore: boolean, total: number }> {
    const from = page * limit;
    const to = from + limit - 1;
    
    const { data, error, count } = await supabase
      .from('issue_comments')
      .select('*', { count: 'exact' })
      .eq('issue_id', issueId)
      .order('created_at', { ascending: true })
      .range(from, to);
    
    if (error) throw error;
    
    const totalCount = count || 0;
    const hasMore = (page + 1) * limit < totalCount;
    
    return { comments: data || [], hasMore, total: totalCount };
  },

  // Add comment to issue
  async addComment(issueId: string, content: string): Promise<IssueComment> {
    const fingerprint = getStoredFingerprint();
    
    // Check if user already commented on this issue
    const { data: existingComment } = await supabase
      .from('issue_comments')
      .select('id')
      .eq('issue_id', issueId)
      .eq('user_fingerprint', fingerprint)
      .maybeSingle();

    if (existingComment) {
      throw new Error('You can only comment once per issue');
    }
    
    const { data, error } = await supabase
      .from('issue_comments')
      .insert({
        issue_id: issueId,
        content,
        user_fingerprint: fingerprint
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Vote on comment
  async voteOnComment(commentId: string, voteType: 'upvote' | 'downvote'): Promise<void> {
    const fingerprint = getStoredFingerprint();
    
    try {
      // Check if user already voted
      const { data: existingVote } = await supabase
        .from('issue_comment_votes')
        .select('*')
        .eq('comment_id', commentId)
        .eq('user_fingerprint', fingerprint)
        .maybeSingle();

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          // Remove vote if clicking same button
          await supabase
            .from('issue_comment_votes')
            .delete()
            .eq('id', existingVote.id);
        } else {
          // Update existing vote
          await supabase
            .from('issue_comment_votes')
            .update({ vote_type: voteType })
            .eq('id', existingVote.id);
        }
      } else {
        // Create new vote - handle conflicts
        const { error: insertError } = await supabase
          .from('issue_comment_votes')
          .insert({
            comment_id: commentId,
            user_fingerprint: fingerprint,
            vote_type: voteType
          });
        
        if (insertError && insertError.code === '23505') {
          // Conflict - user already voted, update instead
          await supabase
            .from('issue_comment_votes')
            .update({ vote_type: voteType })
            .eq('comment_id', commentId)
            .eq('user_fingerprint', fingerprint);
        } else if (insertError) {
          throw insertError;
        }
      }

      // Update vote counts on comment
      await this.updateCommentVoteCounts(commentId);
    } catch (error) {
      console.error('Error voting on comment:', error);
      throw error;
    }
  },

  // Update comment vote counts
  async updateCommentVoteCounts(commentId: string): Promise<void> {
    try {
      const { data: votes } = await supabase
        .from('issue_comment_votes')
        .select('vote_type')
        .eq('comment_id', commentId);

      const upvotes = votes?.filter(v => v.vote_type === 'upvote').length || 0;
      const downvotes = votes?.filter(v => v.vote_type === 'downvote').length || 0;

      const { error } = await supabase
        .from('issue_comments')
        .update({ upvotes, downvotes })
        .eq('id', commentId);

      if (error) {
        console.error('Error updating comment vote counts:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateCommentVoteCounts:', error);
      throw error;
    }
  },

  // Get user's vote for issue
  async getUserVoteForIssue(issueId: string): Promise<string | null> {
    const fingerprint = getStoredFingerprint();
    
    const { data } = await supabase
      .from('issue_votes')
      .select('vote_type')
      .eq('issue_id', issueId)
      .eq('user_fingerprint', fingerprint)
      .maybeSingle();

    return data?.vote_type || null;
  },

  // Get all user votes for issues (batch)
  async getUserVotesForAllIssues(): Promise<Map<string, 'upvote' | 'downvote'>> {
    const fingerprint = getStoredFingerprint();
    
    const { data, error } = await supabase
      .from('issue_votes')
      .select('issue_id, vote_type')
      .eq('user_fingerprint', fingerprint);

    const votesMap = new Map<string, 'upvote' | 'downvote'>();
    if (data) {
      data.forEach(vote => {
        votesMap.set(vote.issue_id, vote.vote_type as 'upvote' | 'downvote');
      });
    }
    return votesMap;
  },

  // Get user's vote for comment
  async getUserVoteForComment(commentId: string): Promise<string | null> {
    const fingerprint = getStoredFingerprint();
    
    const { data } = await supabase
      .from('issue_comment_votes')
      .select('vote_type')
      .eq('comment_id', commentId)
      .eq('user_fingerprint', fingerprint)
      .maybeSingle();

    return data?.vote_type || null;
  },

  // Get all user votes for comments in an issue (batch)
  async getUserVotesForComments(issueId: string): Promise<Map<string, 'upvote' | 'downvote'>> {
    const fingerprint = getStoredFingerprint();
    
    // Get all comment IDs for this issue first
    const { data: comments } = await supabase
      .from('issue_comments')
      .select('id')
      .eq('issue_id', issueId);
    
    if (!comments || comments.length === 0) {
      return new Map();
    }
    
    const commentIds = comments.map(c => c.id);
    
    const { data: votes } = await supabase
      .from('issue_comment_votes')
      .select('comment_id, vote_type')
      .eq('user_fingerprint', fingerprint)
      .in('comment_id', commentIds);

    const votesMap = new Map<string, 'upvote' | 'downvote'>();
    if (votes) {
      votes.forEach(vote => {
        votesMap.set(vote.comment_id, vote.vote_type as 'upvote' | 'downvote');
      });
    }
    return votesMap;
  },

  // Delete comment (only own comments)
  async deleteComment(commentId: string): Promise<void> {
    const fingerprint = getStoredFingerprint();
    
    const { error } = await supabase
      .from('issue_comments')
      .delete()
      .eq('id', commentId)
      .eq('user_fingerprint', fingerprint);
    
    if (error) throw error;
  },

  // Check if comment belongs to user
  async isUserComment(commentId: string): Promise<boolean> {
    const fingerprint = getStoredFingerprint();
    
    const { data } = await supabase
      .from('issue_comments')
      .select('id')
      .eq('id', commentId)
      .eq('user_fingerprint', fingerprint)
      .maybeSingle();
    
    return !!data;
  },

  // Get all user's comment IDs for an issue (batch check)
  async getUserCommentIds(issueId: string): Promise<Set<string>> {
    const fingerprint = getStoredFingerprint();
    
    const { data } = await supabase
      .from('issue_comments')
      .select('id')
      .eq('issue_id', issueId)
      .eq('user_fingerprint', fingerprint);
    
    return new Set(data?.map(c => c.id) || []);
  },

  // Get comment counts for multiple issues
  async getCommentCounts(issueIds: string[]): Promise<Map<string, number>> {
    if (issueIds.length === 0) return new Map();
    
    const { data, error } = await supabase
      .from('issue_comments')
      .select('issue_id')
      .in('issue_id', issueIds);
    
    if (error) throw error;
    
    // Count comments per issue
    const counts = new Map<string, number>();
    issueIds.forEach(id => counts.set(id, 0));
    
    data?.forEach(comment => {
      const current = counts.get(comment.issue_id) || 0;
      counts.set(comment.issue_id, current + 1);
    });
    
    return counts;
  },

  // Delete issue (only own issues)
  async deleteIssue(issueId: string): Promise<void> {
    const fingerprint = getStoredFingerprint();
    
    // First check if this user created the issue
    const { data: issue } = await supabase
      .from('issues')
      .select('id')
      .eq('id', issueId)
      .eq('user_fingerprint', fingerprint)  // Need to add this column
      .maybeSingle();

    if (!issue) {
      throw new Error('You can only delete your own posts');
    }

    const { error } = await supabase
      .from('issues')
      .delete()
      .eq('id', issueId)
      .eq('user_fingerprint', fingerprint);
    
    if (error) throw error;
  },

  // Check if issue belongs to user
  async isUserIssue(issueId: string): Promise<boolean> {
    const fingerprint = getStoredFingerprint();
    
    const { data } = await supabase
      .from('issues')
      .select('id')
      .eq('id', issueId)
      .eq('user_fingerprint', fingerprint)
      .maybeSingle();
    
    return !!data;
  },

  // Update issue status (only for owner)
  async updateIssueStatus(issueId: string, status: 'reported' | 'in_progress' | 'fixed' | 'rejected'): Promise<void> {
    const fingerprint = getStoredFingerprint();
    
    // Verify ownership first
    const isOwner = await this.isUserIssue(issueId);
    if (!isOwner) {
      throw new Error('You can only update your own issues');
    }
    
    const { error } = await supabase
      .from('issues')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', issueId)
      .eq('user_fingerprint', fingerprint);
    
    if (error) throw error;
  }
};